/**
 * SpeakerNotesInput Component
 * Textarea for entering speaker notes for a slide
 * Features:
 * - Character counter
 * - Expandable textarea
 * - Placeholder tips
 */

import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { MessageSquare, Info } from 'lucide-react'

// ============================================
// Types
// ============================================

export interface SpeakerNotesInputProps {
  value: string
  onChange: (notes: string) => void
  placeholder?: string
  maxLength?: number
  disabled?: boolean
  className?: string
}

// ============================================
// SpeakerNotesInput Component
// ============================================

export function SpeakerNotesInput({
  value,
  onChange,
  placeholder = 'Add speaker notes for this slide...',
  maxLength = 5000,
  disabled = false,
  className,
}: SpeakerNotesInputProps) {
  // Calculate stats
  const stats = useMemo(() => {
    const characters = value.length
    const words = value
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length
    const estimatedReadingTime = Math.ceil(words / 150) // Avg speaking rate

    return { characters, words, estimatedReadingTime }
  }, [value])

  // Check if near limit
  const isNearLimit = stats.characters > maxLength * 0.9

  return (
    <div className={cn('space-y-2', className)}>
      <Label className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-neutral-500" />
        Speaker Notes
      </Label>

      <div className="relative">
        <Textarea
          value={value}
          onChange={(e) => {
            const newValue = e.target.value
            if (newValue.length <= maxLength) {
              onChange(newValue)
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          rows={4}
          className={cn(
            'rounded-xl resize-y min-h-[100px]',
            isNearLimit && 'border-amber-300 dark:border-amber-600'
          )}
        />
      </div>

      {/* Stats Footer */}
      <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
        <div className="flex items-center gap-4">
          <span>{stats.words} words</span>
          {stats.estimatedReadingTime > 0 && (
            <span>~{stats.estimatedReadingTime} min speaking time</span>
          )}
        </div>
        <span
          className={cn(
            isNearLimit && 'text-amber-600 dark:text-amber-400 font-medium'
          )}
        >
          {stats.characters.toLocaleString()} / {maxLength.toLocaleString()}
        </span>
      </div>

      {/* Tips */}
      <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <p className="text-xs text-blue-700 dark:text-blue-300 flex items-start gap-2">
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>
            Speaker notes help you remember key points during presentations.
            They won't be visible to your audience during slideshows.
          </span>
        </p>
      </div>
    </div>
  )
}

export default SpeakerNotesInput
